import {
  COMPANY_LABELS,
  SOCIAL_ACCOUNT_KIND_LABELS,
  SOCIAL_PLATFORM_LABELS,
  formatNumber,
  type SocialAccountDto,
} from '@nova/shared';
import { Facebook, Linkedin, Pencil, Plus, TrendingUp } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../components/Button.js';
import { EmptyState } from '../../components/EmptyState.js';
import { GlassPanel } from '../../components/GlassPanel.js';
import { SkeletonPanel } from '../../components/Skeleton.js';
import { cn } from '../../lib/cn.js';
import { SocialAccountForm } from './SocialAccountForm.js';
import { SocialHealthBadge } from './SocialHealthBadge.js';
import { useUpdateAccount, useUpsertMetric } from './useSocial.js';

export function SocialRegister({
  accounts,
  isLoading,
  canEdit,
}: {
  accounts: SocialAccountDto[];
  isLoading: boolean;
  canEdit: boolean;
}) {
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<SocialAccountDto | null>(null);

  function openCreate() {
    setEditing(null);
    setFormOpen(true);
  }
  function openEdit(account: SocialAccountDto) {
    setEditing(account);
    setFormOpen(true);
  }

  return (
    <div className="space-y-3">
      {canEdit && (
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="primary"
            icon={<Plus size={14} aria-hidden />}
            onClick={openCreate}
          >
            Legg til konto
          </Button>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonPanel key={i} lines={2} />
          ))}
        </div>
      ) : accounts.length === 0 ? (
        <GlassPanel>
          <EmptyState
            title="Ingen kontoer i registeret ennå."
            description="Legg til selskapenes LinkedIn- og Facebook-kontoer for å følge tallene."
            action={
              canEdit ? (
                <Button size="sm" icon={<Plus size={14} aria-hidden />} onClick={openCreate}>
                  Legg til konto
                </Button>
              ) : undefined
            }
          />
        </GlassPanel>
      ) : (
        accounts.map((account) => (
          <AccountRow key={account.id} account={account} canEdit={canEdit} onEdit={openEdit} />
        ))
      )}

      {formOpen && (
        <SocialAccountForm open={formOpen} onClose={() => setFormOpen(false)} account={editing} />
      )}
    </div>
  );
}

function AccountRow({
  account,
  canEdit,
  onEdit,
}: {
  account: SocialAccountDto;
  canEdit: boolean;
  onEdit: (account: SocialAccountDto) => void;
}) {
  const [entryOpen, setEntryOpen] = useState(false);
  const toggleActive = useUpdateAccount();
  const Platform = account.platform === 'FACEBOOK' ? Facebook : Linkedin;

  return (
    <GlassPanel accentColor={account.area?.color} className={cn(!account.isActive && 'opacity-60')}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-ink-soft flex items-center gap-1.5 text-[11px]">
            <Platform size={13} aria-hidden />
            {SOCIAL_PLATFORM_LABELS[account.platform]} · {SOCIAL_ACCOUNT_KIND_LABELS[account.kind]}{' '}
            · {COMPANY_LABELS[account.company]}
          </div>
          <div className="mt-0.5 flex items-center gap-2">
            <h3 className="font-display text-ink truncate text-sm">{account.name}</h3>
            {!account.isActive && <span className="text-ink-soft text-[11px]">(inaktiv)</span>}
          </div>
          <div className="text-ink-soft mt-1 flex items-center gap-3 text-[11px]">
            <span>
              {account.latest?.followers != null
                ? `${formatNumber(account.latest.followers)} følgere`
                : 'Ingen tall ennå'}
            </span>
            <SocialHealthBadge flag={account.health} />
          </div>
        </div>

        <div className="flex items-center gap-2">
          {canEdit && (
            <Button
              size="sm"
              icon={<TrendingUp size={13} aria-hidden />}
              onClick={() => setEntryOpen((v) => !v)}
            >
              Legg inn tall
            </Button>
          )}
          {canEdit && (
            <Button
              size="sm"
              variant="ghost"
              icon={<Pencil size={13} aria-hidden />}
              onClick={() => onEdit(account)}
              aria-label="Rediger"
            />
          )}
          {canEdit && (
            <Button
              size="sm"
              variant="ghost"
              loading={toggleActive.isPending}
              onClick={() =>
                toggleActive.mutate({ id: account.id, body: { isActive: !account.isActive } })
              }
            >
              {account.isActive ? 'Deaktiver' : 'Aktiver'}
            </Button>
          )}
        </div>
      </div>

      {entryOpen && canEdit && <MetricEntry account={account} onDone={() => setEntryOpen(false)} />}
    </GlassPanel>
  );
}

const entryField =
  'border-hairline bg-base/50 text-ink h-9 w-full rounded-lg border px-2.5 text-sm';

function MetricEntry({ account, onDone }: { account: SocialAccountDto; onDone: () => void }) {
  const upsert = useUpsertMetric(account.id);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [followers, setFollowers] = useState('');
  const [reach, setReach] = useState('');
  const [engagements, setEngagements] = useState('');
  const [posts, setPosts] = useState('');

  const parse = (v: string): number | null => {
    const n = Number(v.replace(/\s/g, '').replace(',', '.'));
    return v.trim() === '' || Number.isNaN(n) ? null : Math.round(n);
  };

  async function save() {
    await upsert.mutateAsync({
      period: `${month}-01`,
      followers: parse(followers),
      reach: parse(reach),
      engagements: parse(engagements),
      posts: parse(posts),
      source: 'MANUAL',
    });
    onDone();
  }

  const nothing = !followers && !reach && !engagements && !posts;

  return (
    <div className="border-hairline bg-base/30 mt-4 rounded-lg border p-3">
      <p className="label-micro mb-2">Tall for måneden</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        <label className="flex flex-col gap-1">
          <span className="text-ink-soft text-[11px]">Måned</span>
          <input
            type="month"
            className={entryField}
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-ink-soft text-[11px]">Følgere</span>
          <input
            inputMode="numeric"
            className={entryField}
            value={followers}
            onChange={(e) => setFollowers(e.target.value)}
            placeholder="0"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-ink-soft text-[11px]">Visninger</span>
          <input
            inputMode="numeric"
            className={entryField}
            value={reach}
            onChange={(e) => setReach(e.target.value)}
            placeholder="0"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-ink-soft text-[11px]">Engasjement</span>
          <input
            inputMode="numeric"
            className={entryField}
            value={engagements}
            onChange={(e) => setEngagements(e.target.value)}
            placeholder="0"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-ink-soft text-[11px]">Innlegg</span>
          <input
            inputMode="numeric"
            className={entryField}
            value={posts}
            onChange={(e) => setPosts(e.target.value)}
            placeholder="0"
          />
        </label>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button
          size="sm"
          variant="primary"
          disabled={nothing}
          loading={upsert.isPending}
          onClick={() => void save()}
        >
          Lagre måned
        </Button>
        <Button size="sm" onClick={onDone}>
          Lukk
        </Button>
        {upsert.isError && (
          <span className="text-danger text-xs">{(upsert.error as Error).message}</span>
        )}
        <span className="text-ink-soft ml-auto text-[11px]">
          Samme måned på nytt oppdaterer tallene.
        </span>
      </div>
    </div>
  );
}
